import { FirestoreCollections, ID, Locales } from '@griply/types';

import { detachWrite } from '../detach-write';
import { FirestoreInstance } from '../types/firestore';

export type CreatePushTokenOptions<
  TDocRef = unknown,
  TFieldValue = unknown,
  TDb = FirestoreInstance,
> = {
  userId: ID;
  token: ID;
  type: string;
  platform: string;
  version: string;
  timezone: string;
  language: Locales;
  // When provided, the document is keyed by the stable installation id so each
  // device owns exactly one row (rotations overwrite instead of accumulating).
  installationId?: ID;
  apnsToken?: ID;
  authorizationStatus?: number;
  osVersion?: string;
  db: TDb;
  doc: (db: NoInfer<TDb>, path: string, ...rest: string[]) => TDocRef;
  setDoc: (
    ref: NoInfer<TDocRef>,
    data: Record<string, unknown>,
  ) => Promise<void> | void;
  serverTimestamp: () => TFieldValue;
  onWriteError: (error: unknown) => void;
};

export const createPushToken = async <
  TDocRef,
  TFieldValue,
  TDb = FirestoreInstance,
>({
  userId,
  token,
  type,
  platform,
  version,
  timezone,
  language,
  installationId,
  apnsToken,
  authorizationStatus,
  osVersion,
  db,
  doc,
  setDoc,
  serverTimestamp,
  onWriteError,
}: CreatePushTokenOptions<TDocRef, TFieldValue, TDb>): Promise<void> => {
  if (!userId || !token) {
    return;
  }
  const ref = doc(db, FirestoreCollections.FcmTokens, installationId ?? token);
  const data: Record<string, unknown> = {
    token,
    uid: userId,
    type,
    platform,
    version,
    timezone,
    language,
    updatedAt: serverTimestamp(),
  };
  if (installationId) {
    data.installationId = installationId;
  }
  if (apnsToken) {
    data.apnsToken = apnsToken;
  }
  if (typeof authorizationStatus === 'number') {
    data.authorizationStatus = authorizationStatus;
  }
  if (osVersion) {
    data.osVersion = osVersion;
  }
  detachWrite(setDoc(ref, data), onWriteError);
};
